import getVoxelThicknessAlongNormal from '../getVoxelThicknessAlongNormal.js';
import { projectPointOntoPlane } from '../slabMembership.js';
import { UNBOUNDED, createColumnLineResolver, createPlaneBasis, SHAPE_BOUNDARY_EPSILON, intersectRanges, solveAbsLinearLeq, toIntegerRun, } from './shapeGeometry.js';
const expand = (halfExtent) => halfExtent + Math.max(halfExtent, 1) * SHAPE_BOUNDARY_EPSILON;
export function createRectangleShape(options) {
    const { volume, planePoint, viewPlaneNormal: normal, majorAxis, majorHalfLength, minorHalfLength, depthHalfLength, } = options;
    if (!(majorHalfLength > 0) || !(minorHalfLength > 0)) {
        throw new Error('Rectangle half lengths must be positive');
    }
    if (depthHalfLength !== undefined && !(depthHalfLength > 0)) {
        throw new Error('Rectangle depthHalfLength must be positive when supplied');
    }
    const basis = createPlaneBasis(normal, majorAxis);
    const isSolid = depthHalfLength !== undefined;
    const voxelThickness = getVoxelThicknessAlongNormal(volume, basis.n);
    const depthExtent = isSolid
        ? depthHalfLength + voxelThickness / 2
        : 0;
    const center = isSolid
        ? [...options.centerWorld]
        : projectPointOntoPlane(options.centerWorld, planePoint, basis.n);
    const constraints = [
        { axis: basis.u, halfExtent: expand(majorHalfLength) },
        { axis: basis.v, halfExtent: expand(minorHalfLength) },
    ];
    if (isSolid) {
        constraints.push({ axis: basis.n, halfExtent: expand(depthExtent) });
    }
    const resolveColumnLine = createColumnLineResolver(volume, basis.n);
    function containsPoint(point) {
        const tested = isSolid
            ? point
            : projectPointOntoPlane(point, planePoint, basis.n);
        for (const { axis, halfExtent } of constraints) {
            const offset = (tested[0] - center[0]) * axis[0] +
                (tested[1] - center[1]) * axis[1] +
                (tested[2] - center[2]) * axis[2];
            if (Math.abs(offset) > halfExtent) {
                return false;
            }
        }
        return true;
    }
    function* getRuns(outerIndex, rowIndex, _depthRun, slab) {
        const line = resolveColumnLine(slab, outerIndex, rowIndex);
        const lineBase = isSolid ? line.base : line.projectedBase;
        const lineStep = isSolid ? line.step : line.projectedStep;
        let range = UNBOUNDED;
        for (const { axis, halfExtent } of constraints) {
            const offset = (lineBase[0] - center[0]) * axis[0] +
                (lineBase[1] - center[1]) * axis[1] +
                (lineBase[2] - center[2]) * axis[2];
            const slope = lineStep[0] * axis[0] + lineStep[1] * axis[1] + lineStep[2] * axis[2];
            range = intersectRanges(range, solveAbsLinearLeq(offset, slope, halfExtent));
            if (!range) {
                return;
            }
        }
        const run = toIntegerRun(range);
        if (run) {
            yield run;
        }
    }
    return {
        containsPoint,
        getRuns,
        getRequiredThickness: () => (isSolid ? depthExtent * 2 : 0),
    };
}
